import React, { useEffect, useRef } from "react"

import {
  currentPageWithoutSensitiveParams,
  resetMosChat,
  resolveMosChatOrigin,
  showMosChat
} from "@masteryprep/mp-shared-components/utils"

import { firebaseauth } from "../../mp-database/firebaseConfig"
import { checkFeatureFlag } from "../../mp-utils/generic-utils/feature-flags"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"
import { getEnv } from "../../../utils/env"

interface MosChatUser {
  id?: string
  uid?: string
  role?: string
}

interface MosChatManagerProps {
  user?: MosChatUser | null
  isAuthed?: boolean
  isAuthing: boolean
}

const getUserId = (user?: MosChatUser | null): string | null =>
  user?.id?.trim() || user?.uid?.trim() || null

const getIdentityToken = async (): Promise<string> => {
  const currentUser = firebaseauth.currentUser
  if (!currentUser) throw new Error("Ready authentication is unavailable")

  const firebaseIdToken = await currentUser.getIdToken(false)
  const functionRoot = getEnv("CLOUD_FUNCTION_ROOT").replace(/\/$/, "")
  if (!functionRoot) throw new Error("Ready function origin is unavailable")

  const response = await fetch(`${functionRoot}/mosChatGroup-identityToken`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${firebaseIdToken}`,
      "Content-Type": "application/json"
    },
    body: "{}"
  })
  if (!response.ok) throw new Error("MOS identity is unavailable")

  const body = (await response.json()) as {
    token?: unknown
    expiresAt?: unknown
  }
  if (
    typeof body.token !== "string" ||
    body.token.trim() === "" ||
    typeof body.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(body.expiresAt))
  ) {
    throw new Error("MOS identity response was invalid")
  }
  return body.token
}

const MosChatManager: React.FC<MosChatManagerProps> = ({
  user,
  isAuthed,
  isAuthing
}) => {
  const generationRef = useRef(0)
  const previousUserIdRef = useRef<string | null | undefined>(undefined)

  const userId = getUserId(user)
  const eligible =
    !isAuthing &&
    isAuthed === true &&
    userId !== null &&
    typeof user?.role === "string" &&
    user.role !== "Student"

  useEffect(() => {
    // Auth starts indeterminate while Firebase restores its persisted user. Treating that as a
    // logout would erase the same user's resumable MOS conversation on every ordinary reload.
    if (isAuthing || isAuthed === undefined) return undefined

    const generation = ++generationRef.current
    const activeUserId = eligible ? userId : null
    const previousUserId = previousUserIdRef.current
    previousUserIdRef.current = activeUserId

    if (
      (previousUserId === undefined && activeUserId === null) ||
      (previousUserId !== undefined && previousUserId !== activeUserId)
    ) {
      resetMosChat()
    }
    if (!eligible || !activeUserId) return undefined

    const origin = resolveMosChatOrigin({
      origin: getEnv("MOS_CHAT_ORIGIN"),
      allowedHosts: getEnv("MOS_CHAT_ALLOWED_HOSTS")
    })
    if (!origin) {
      console.warn("[MOS Chat] Widget configuration is unavailable")
      resetMosChat()
      return undefined
    }

    void checkFeatureFlag(FeatureFlagName.mos_chat_widget)
      .then(async flag => {
        if (generationRef.current !== generation) return
        if (!flag.enabled) {
          resetMosChat()
          return
        }
        await showMosChat(origin, {
          getIdentityToken,
          getPageUrl: currentPageWithoutSensitiveParams
        })
      })
      .catch(() => {
        if (generationRef.current !== generation) return
        console.warn("[MOS Chat] Widget could not be enabled")
        resetMosChat()
      })

    return () => {
      generationRef.current += 1
    }
  }, [eligible, isAuthing, isAuthed, userId])

  return null
}

export default MosChatManager
